import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { useConfirmation } from '@/components/shared/ZCard';
import ZCard from '@/components/shared/ZCard';
import ZTabs from '@/components/shared/ZTabs';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import { SYSTEM_COLORS } from '@/lib/constants';
import {
  ChevronLeft, ChevronRight, Check, Upload, Plus, Trash2, TrendingUp,
  TrendingDown, Loader2, AlertCircle, FileText, X, Pencil,
} from 'lucide-react';

const FLUX = '#E24B4A';
const TRAVEL_BLUE = '#4C93D6';
const T1 = SYSTEM_COLORS.textPrimary;
const T2 = SYSTEM_COLORS.textSecondary;
const T3 = SYSTEM_COLORS.textTertiary;

const ZAR = (n) =>
  'R' + (n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const base = y * 12 + (mo - 1) + delta;
  return `${Math.floor(base / 12)}-${String((base % 12) + 1).padStart(2, '0')}`;
};
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
};
// Whole-rand, space-thousands ("R56 448"): the convention used in the mediator documents.
const ZAR0 = (n) => 'R' + Math.round(n || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 }).replace(/[,\u00a0]/g, ' ');

// Consent Paper (Case No. 10319/19) clause 8 buckets. Order is fixed and mirrors the document.
const CLAUSE_OPTIONS = ['8.1', '8.2', '8.3', '8.3-cash', 'outside', 'travel'];
const CLAUSE_SECTIONS = [
  { key: '8.1', title: '8.1  School fees, extramural', total: '8.1 total' },
  { key: '8.2', title: '8.2  Medical aid, medical', total: '8.2 total' },
  { key: '8.3', title: '8.3  Food/toiletries, childcare, clothing, transport', total: "8.3 paid directly in W du Toit's household" },
  { key: '8.3-cash', title: '8.3  Cash contribution paid to M du Toit', total: 'Cash paid to M du Toit' },
  { key: 'outside', title: 'Outside clause', total: 'Outside clause total' },
  { key: 'travel', title: 'Travel', total: 'Travel total' },
];
// Display-only overrides for well-known categories. Bucketing never depends on these.
const CLAUSE_LABELS = {
  'school-fees': 'School fees',
  'sport-recreation': 'Extramural',
  'discovery-health': "Medical aid, Discovery Health (children's share of premium)",
  'discovery-life': 'Life cover, Discovery Life, minor children as beneficiaries',
  medical: 'Medical',
  'groceries & dining': 'Food and toiletries',
  transport: 'Transport',
};

// ==================== DASHBOARD ====================
function DashboardTab() {
  const [win, setWin] = useState(3);        // rolling-average window: 3 | 6 | 12 | 999, or 'custom'
  const [range, setRange] = useState({ from: '', to: '' }); // YYYY-MM bounds when win === 'custom'
  const [view, setView] = useState('category'); // 'category' | 'clause'
  const [summary, setSummary] = useState(null);
  const [colors, setColors] = useState({});
  const [clauses, setClauses] = useState({});   // name -> clause | null
  const [catNames, setCatNames] = useState([]); // non-system category names
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // category drill-down
  const [catSplits, setCatSplits] = useState({}); // name -> {p, k}
  const [editCat, setEditCat] = useState(null);   // category whose split is being edited
  const [csDraft, setCsDraft] = useState({ p: '', k: '' });

  const loadCategories = useCallback(() => {
    api.get(`/flux/categories?_=${Date.now()}`).then(({ data }) => {
      const cm = {}, sm = {}, cl = {}, names = [];
      (data.categories || []).forEach((c) => {
        cm[c.name] = c.color;
        sm[c.name] = { p: c.personal_pct ?? 100, k: c.kids_pct ?? 0 };
        cl[c.name] = c.clause || null;
        if (!c.system) names.push(c.name);
      });
      setColors(cm); setCatSplits(sm); setClauses(cl); setCatNames(names);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const saveCatSplit = async (name) => {
    const p = Math.max(0, Math.min(100, Number(csDraft.p) || 0));
    const k = Math.max(0, Math.min(100, Number(csDraft.k) || 0));
    try {
      await api.patch(`/flux/categories/${encodeURIComponent(name)}/split`, { personal_pct: p, kids_pct: k });
      setCatSplits((s) => ({ ...s, [name]: { p, k } }));
      setEditCat(null);
      setLoading(true); loadSummary();
    } catch { /* */ }
  };
  const loadSummary = useCallback(() => {
    // cache-buster so splits / not-mine changes are always reflected
    api.get(`/flux/summary?months=36&_=${Date.now()}`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { setLoading(true); loadSummary(); }, [loadSummary]);

  // Exclude the current, still-incomplete calendar month so rolling averages use full
  // months only. A custom range may include it deliberately.
  const allMonths = summary?.months || [];
  const months = allMonths.filter((m) => m.month < thisMonth());
  const custom = win === 'custom';
  const rFrom = range.from || (allMonths[0] && allMonths[0].month) || '';
  const rTo = range.to || (allMonths.length ? allMonths[allMonths.length - 1].month : '');
  const win_ = custom
    ? allMonths.filter((m) => m.month >= rFrom && m.month <= rTo)
    : months.slice(-win);                    // last N full months, driven by the toggle
  const wn = win_.length || 1;
  const wTotal = win_.reduce((a, m) => a + (m.total || 0), 0);
  const wKids = win_.reduce((a, m) => a + (m.kids || 0), 0);
  const avgWin = wTotal / wn;                 // total spend/month over the window
  const kidsAvgWin = wKids / wn;              // kids spend/month
  const youAvgWin = avgWin - kidsAvgWin;      // your own spend/month
  const kidsPct = wTotal ? Math.round((wKids / wTotal) * 100) : 0;
  const shortMonth = (m) => {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
  };
  const winLabel = custom
    ? (rFrom === rTo ? shortMonth(rFrom) : `${shortMonth(rFrom)} to ${shortMonth(rTo)}`)
    : (win >= months.length ? 'all time' : `last ${win}mo`);
  const perMonth = wn === 1 ? '' : ' / month';

  // Pull travel out as its own line: you / kids  ×  everyday / travel.
  const wTravPers = win_.reduce((a, m) => a + ((m.categories && m.categories.travel) || 0), 0);
  const wTravKids = win_.reduce((a, m) => a + ((m.categories_kids && m.categories_kids.travel) || 0), 0);
  const youTrav = (wTravPers - wTravKids) / wn;  // your travel, own share
  const kidsTrav = wTravKids / wn;               // kids' travel
  const youBase = youAvgWin - youTrav;           // your everyday own (cost of living)
  const kidsBase = kidsAvgWin - kidsTrav;        // kids' everyday
  const travTotal = wTravPers / wn;              // combined travel (you + kids)
  const everydayTotal = avgWin - travTotal;      // everyday (you + kids), sums with travel to total

  const catAgg = {};
  const catKids = {};     // kids' share per category over the window
  const catMonths = {};   // months in the window where this category actually has spend
  const entityMix = {};   // personal share by paying entity over the window
  win_.forEach((m) => {
    Object.entries(m.categories || {}).forEach(([c, v]) => {
      catAgg[c] = (catAgg[c] || 0) + v;
      if (v > 0) catMonths[c] = (catMonths[c] || 0) + 1;
    });
    Object.entries(m.categories_kids || {}).forEach(([c, v]) => { catKids[c] = (catKids[c] || 0) + v; });
    Object.entries(m.entity_mix || {}).forEach(([e, v]) => { entityMix[e] = (entityMix[e] || 0) + v; });
  });
  const catRows = Object.entries(catAgg).sort((a, b) => b[1] - a[1]);
  const catMax = catRows.length ? catRows[0][1] : 1;

  if (loading) return <ZLoadingBar />;
  if (!months.length) {
    return <ZEmptyState mark="F" accent={FLUX} message="No spend yet" hint="Ingest statements and lock a taxonomy to see the picture." />;
  }

  return (
    <div style={{ paddingBottom: 120 }}>
      {/* window toggle drives everything below */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: custom ? 10 : 16, flexWrap: 'wrap' }}>
        {[{ v: 3, l: '3mo' }, { v: 6, l: '6mo' }, { v: 12, l: '12mo' }, { v: 999, l: 'All time' }, { v: 'custom', l: 'Pick' }].map(({ v, l }) => (
          <button key={v} onClick={() => {
            setWin(v);
            if (v === 'custom' && !range.from && months.length) {
              const last = months[months.length - 1].month;
              setRange({ from: last, to: last });
            }
          }} style={{
            ...pill, padding: '7px 15px', fontWeight: 600,
            ...(win === v ? { background: `${FLUX}22`, color: FLUX, borderColor: `${FLUX}55` } : {}),
          }}>{l}</button>
        ))}
      </div>
      {custom && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={rFrom} onChange={(e) => {
            const from = e.target.value;
            setRange((r) => ({ from, to: r.to && r.to < from ? from : (r.to || from) }));
          }} style={selectStyle}>
            {allMonths.map((m) => <option key={m.month} value={m.month}>{shortMonth(m.month)}</option>)}
          </select>
          <span style={{ color: T3, fontSize: 12 }}>to</span>
          <select value={rTo} onChange={(e) => {
            const to = e.target.value;
            setRange((r) => ({ from: r.from && r.from > to ? to : (r.from || to), to }));
          }} style={selectStyle}>
            {allMonths.map((m) => <option key={m.month} value={m.month}>{shortMonth(m.month)}</option>)}
          </select>
          <span style={{ color: T3, fontSize: 11 }}>
            {wn} month{wn === 1 ? '' : 's'}{rTo >= thisMonth() ? ', current month incomplete' : ''}
          </span>
        </div>
      )}
      {/* view toggle: category breakdown vs Consent Paper clause grouping */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
        {[{ v: 'category', l: 'By category' }, { v: 'clause', l: 'By clause' }].map(({ v, l }) => (
          <button key={v} onClick={() => setView(v)} style={{
            ...pill, padding: '5px 13px',
            ...(view === v ? { background: 'rgba(232,161,58,0.14)', color: '#E8A13A', borderColor: 'rgba(232,161,58,0.4)' } : {}),
          }}>{l}</button>
        ))}
      </div>

      {/* Everyday cost of living — travel pulled out as its own line */}
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ color: T3, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>Your normal spend{perMonth} · {winLabel}</div>
        <div style={{ color: T1, fontSize: 46, fontWeight: 700, lineHeight: 1.05, marginTop: 4 }}>{ZAR(youBase)}</div>
        <div style={{ color: T3, fontSize: 12, marginTop: 5 }}>your own, excluding kids and travel</div>
      </div>

      {/* four-way split: you / kids  ×  everyday / travel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        {[
          { label: 'YOU · NORMAL SPEND', v: youBase, accent: FLUX, strong: true },
          { label: 'YOU · TRAVEL ✈', v: youTrav, accent: FLUX, strong: false },
          { label: 'KIDS · NORMAL SPEND', v: kidsBase, accent: '#E8A13A', strong: true },
          { label: 'KIDS · TRAVEL ✈', v: kidsTrav, accent: '#E8A13A', strong: false },
        ].map((tile) => (
          <div key={tile.label} style={{
            background: `${tile.accent}${tile.strong ? '18' : '0c'}`,
            border: `1px solid ${tile.accent}${tile.strong ? '4d' : '2a'}`,
            borderRadius: 12, padding: '11px 13px',
          }}>
            <div style={{ color: tile.accent, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4 }}>{tile.label}</div>
            <div style={{ color: T1, fontSize: 21, fontWeight: 700, marginTop: 3 }}>{ZAR(tile.v)}</div>
          </div>
        ))}
      </div>

      {/* total incl. kids and travel */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12, padding: '11px 14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div style={{ color: T3, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>TOTAL{perMonth.toUpperCase()}</div>
          <div style={{ color: T1, fontSize: 22, fontWeight: 700 }}>{ZAR(avgWin)}</div>
        </div>
        <div style={{ color: T3, fontSize: 11, marginTop: 3 }}>
          normal spend {ZAR(everydayTotal)} · travel {ZAR(travTotal)} · kids are {kidsPct}% of the total
        </div>
      </div>
      <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'rgba(255,255,255,0.05)', marginBottom: 26 }}>
        <div style={{ width: `${(youAvgWin / (avgWin || 1)) * 100}%`, background: FLUX }} />
        <div style={{ width: `${(kidsAvgWin / (avgWin || 1)) * 100}%`, background: '#E8A13A' }} />
      </div>

      {/* monthly trend (context, all months) */}
      <div style={{ color: T3, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Monthly spend (full history)</div>
      <TrendChart series={months} onPick={(m) => setDrill({ month: m, label: monthLabel(m), color: FLUX })} />
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: T3 }}>
        <span><span style={{ color: '#E8A13A' }}>■</span> kids</span>
        <span><span style={{ color: FLUX, opacity: 0.5 }}>■</span> you</span>
        <span><span style={{ color: TRAVEL_BLUE }}>■</span> travel</span>
        <span>tap a month to drill in</span>
      </div>

      {view === 'clause' && (
        <ClauseTable catKids={catKids} wn={wn} clauses={clauses} catNames={catNames} colors={colors}
          winLabel={winLabel} entityMix={entityMix} onDrill={(cat) => setDrill({ name: cat, color: colors[cat] || FLUX })} />
      )}

      {/* spend by category, averaged over the window */}
      {view === 'category' && (<>
      <div style={{ marginTop: 28, marginBottom: 12, color: T3, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Spend by category  <span style={{ color: T3, textTransform: 'none' }}>({wn === 1 ? winLabel : `avg / month, ${winLabel}`})</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {catRows.map(([cat, val]) => (
          <div key={cat} onClick={() => setDrill({ name: cat, color: colors[cat] || FLUX })}
            style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: colors[cat] || FLUX, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: T1, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {cat}<ChevronRight size={13} style={{ color: T3 }} />
                </div>
                {(catKids[cat] || 0) > 0 && (
                  <div style={{ fontSize: 11 }}>
                    <span style={{ color: FLUX }}>You {ZAR((val - (catKids[cat] || 0)) / (catMonths[cat] || 1))}</span>
                    <span style={{ color: '#E8A13A' }}>  ·  Kids {ZAR((catKids[cat] || 0) / (catMonths[cat] || 1))}</span>
                  </div>
                )}
                {editCat === cat ? (
                  <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                    <input type="number" min="0" max="100" value={csDraft.p} onChange={(e) => setCsDraft((d) => ({ ...d, p: e.target.value }))} style={{ ...inputStyle, width: 44, padding: '3px 5px' }} />
                    <span style={{ color: T3, fontSize: 10 }}>you%</span>
                    <input type="number" min="0" max="100" value={csDraft.k} onChange={(e) => setCsDraft((d) => ({ ...d, k: e.target.value }))} style={{ ...inputStyle, width: 44, padding: '3px 5px' }} />
                    <span style={{ color: T3, fontSize: 10 }}>kids%</span>
                    <button onClick={() => saveCatSplit(cat)} style={{ ...pill, padding: '3px 9px', fontSize: 11, background: FLUX, color: '#fff', borderColor: FLUX }}>Save</button>
                    <button onClick={() => setEditCat(null)} style={{ ...pill, padding: '3px 7px', fontSize: 11 }}>×</button>
                  </div>
                ) : (
                  <div onClick={(e) => { e.stopPropagation(); setEditCat(cat); setCsDraft({ p: String(catSplits[cat]?.p ?? 100), k: String(catSplits[cat]?.k ?? 0) }); }}
                    style={{ color: T3, fontSize: 11, marginTop: 2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {catSplits[cat]?.p ?? 100}% you · {catSplits[cat]?.k ?? 0}% kids <Pencil size={10} />
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: T1, fontSize: 14, fontWeight: 700 }}>
                  {ZAR(val / (catMonths[cat] || 1))}<span style={{ color: T3, fontSize: 11, fontWeight: 400 }}> /mo</span>
                </div>
                <div style={{ color: T3, fontSize: 11 }}>{ZAR(val)} over {catMonths[cat] || 0}mo</div>
              </div>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
              <div style={{ width: `${(val / catMax) * 100}%`, height: '100%', background: colors[cat] || FLUX, opacity: 0.75 }} />
            </div>
          </div>
        ))}
      </div>
      </>)}

      {summary?.travel?.total > 0 && (
        <div style={{ marginTop: 30 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div style={{ color: T3, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Travel</div>
            <div style={{ color: T1, fontSize: 22, fontWeight: 700 }}>{ZAR(summary.travel.total)}</div>
          </div>
          <div style={{ color: T3, fontSize: 11, marginTop: 2, marginBottom: 12, textAlign: 'right' }}>
            {ZAR(summary.travel.in_trip)} in-trip · {ZAR(summary.travel.outside_trips)} pre-booked
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...(summary.trips || [])].filter((t) => t.stamped_total > 0).sort((a, b) => (a.start < b.start ? 1 : -1)).map((t) => (
              <div key={t.id} onClick={() => setDrill({ trip_id: t.id, label: t.label, color: FLUX })}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0', cursor: 'pointer' }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: FLUX, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T1, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                    <ChevronRight size={13} style={{ color: T3, flexShrink: 0 }} />
                  </div>
                  <div style={{ color: T3, fontSize: 11 }}>{t.start} → {t.end} · {t.days} days{t.source === 'manual' ? ' · manual' : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: T1, fontSize: 14, fontWeight: 700 }}>{ZAR(t.stamped_total)}</div>
                  <div style={{ color: T3, fontSize: 11 }}>{ZAR(t.cost_per_day)}/day</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {drill && <CategoryDrill cat={drill} onClose={() => { setDrill(null); setLoading(true); loadSummary(); loadCategories(); }} />}
    </div>
  );
}

// Children's share of spend regrouped by Consent Paper clause. Pure presentation over the
// same catKids the category view uses; every category lands in exactly one bucket
// (unmapped -> outside, badged). Per-month divisor is months in window, not months with spend.
function ClauseTable({ catKids, wn, clauses, catNames, colors, winLabel, entityMix, onDrill }) {
  const [copied, setCopied] = useState(false);
  // Every non-system category, plus anything with kids spend the category list doesn't know.
  const names = Array.from(new Set([...catNames, ...Object.keys(catKids)]));
  const buckets = {};
  CLAUSE_SECTIONS.forEach((sec) => { buckets[sec.key] = []; });
  names.forEach((cat) => {
    const cl = clauses[cat];
    const key = CLAUSE_OPTIONS.includes(cl) ? cl : 'outside';
    buckets[key].push({ cat, v: catKids[cat] || 0, unmapped: !CLAUSE_OPTIONS.includes(cl) });
  });
  Object.values(buckets).forEach((rows) => rows.sort((a, b) => b.v - a.v || a.cat.localeCompare(b.cat)));
  const sum = (key) => buckets[key].reduce((a, r) => a + r.v, 0);
  const totals = Object.fromEntries(CLAUSE_SECTIONS.map((sec) => [sec.key, sum(sec.key)]));
  const cash = totals['8.3-cash'];
  const exclCash = totals['8.1'] + totals['8.2'] + totals['8.3'] + totals.outside + totals.travel;
  const inclCash = exclCash + cash;
  // Document order: ex-travel figures first, travel added at the end.
  const grand = [
    { label: 'Total excluding travel, excluding cash to M du Toit', v: exclCash - totals.travel },
    { label: 'Total excluding travel, including cash to M du Toit', v: inclCash - totals.travel },
    { label: 'Total including travel, excluding cash to M du Toit', v: exclCash },
    { label: 'Total including travel and cash to M du Toit', v: inclCash },
  ];
  // Funding: personal share by paying entity over the window (all spend, not children-only).
  const funding = [
    { label: 'Paid from own accounts', v: entityMix.personal || 0 },
    { label: 'Paid by Fenix Rising (personal share of company-paid bills)', v: entityMix.fenix || 0 },
  ];
  const fundingOther = Object.entries(entityMix).filter(([e]) => e !== 'personal' && e !== 'fenix');
  fundingOther.forEach(([e, v]) => funding.push({ label: `Paid by ${e}`, v }));
  funding.push({ label: 'Total paid', v: funding.reduce((a, f) => a + f.v, 0), strong: true });
  const label = (r) => (CLAUSE_LABELS[r.cat] || r.cat) + (r.unmapped ? ' (unmapped)' : '');

  const copy = async () => {
    const lines = [`Children's share of spend · ${winLabel}\tPer month\tWindow total`];
    CLAUSE_SECTIONS.forEach((sec) => {
      lines.push(sec.title);
      buckets[sec.key].forEach((r) => lines.push(`${label(r)}\t${ZAR0(r.v / wn)}\t${ZAR0(r.v)}`));
      lines.push(`${sec.total}\t${ZAR0(totals[sec.key] / wn)}\t${ZAR0(totals[sec.key])}`);
    });
    grand.forEach((g) => lines.push(`${g.label}\t${ZAR0(g.v / wn)}\t${ZAR0(g.v)}`));
    lines.push('Funding');
    funding.forEach((f) => lines.push(`${f.label}\t${ZAR0(f.v / wn)}\t${ZAR0(f.v)}`));
    const text = lines.join('\n');
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  const cell = { padding: '6px 8px', fontSize: 13, whiteSpace: 'nowrap' };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const subRow = { background: 'rgba(255,255,255,0.03)', fontWeight: 700 };
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ color: T3, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Children's share by clause  <span style={{ textTransform: 'none' }}>({winLabel}, {wn}mo)</span>
        </div>
        <button onClick={copy} style={{ ...pill, padding: '5px 12px', ...(copied ? { color: '#4CD68A', borderColor: 'rgba(76,214,138,0.5)' } : {}) }}>
          {copied ? 'Copied' : 'Copy table'}
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: T3, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Clause 8</th>
              <th style={{ ...num, fontWeight: 600 }}>Per month</th>
              <th style={{ ...num, fontWeight: 600 }}>Window</th>
            </tr>
          </thead>
          <tbody>
            {CLAUSE_SECTIONS.map((sec) => (
              <FragmentRows key={sec.key} sec={sec} rows={buckets[sec.key]} total={totals[sec.key]} wn={wn}
                colors={colors} onDrill={onDrill} label={label} cell={cell} num={num} subRow={subRow} />
            ))}
            {grand.map((g, i) => (
              <tr key={g.label} style={{ ...subRow, borderTop: i === 0 ? '1px solid rgba(255,255,255,0.14)' : 'none', color: T1 }}>
                <td style={cell}>{g.label}</td>
                <td style={num}>{ZAR(g.v / wn)}</td>
                <td style={num}>{ZAR(g.v)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} style={{ ...cell, color: '#E8A13A', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, paddingTop: 12 }}>Funding (personal share, all spend)</td>
            </tr>
            {funding.map((f) => (
              <tr key={f.label} style={{ color: T1, ...(f.strong ? subRow : {}) }}>
                <td style={{ ...cell, whiteSpace: 'normal' }}>{f.label}</td>
                <td style={num}>{ZAR(f.v / wn)}</td>
                <td style={num}>{ZAR(f.v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ color: T3, fontSize: 11, marginTop: 12, lineHeight: 1.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div>1. Travel is a flat 35% children's share across all personal travel, including trips the children did not attend. It is an upper bound and is not put forward as a shared cost.</div>
        <div>2. Outside-clause items are shown for completeness and are not put forward as shared costs.</div>
        <div>3. The cash line is the amount actually paid in the window, not the scheduled amount.</div>
      </div>
    </div>
  );
}

function FragmentRows({ sec, rows, total, wn, colors, onDrill, label, cell, num, subRow }) {
  return (
    <>
      <tr>
        <td colSpan={3} style={{ ...cell, color: '#E8A13A', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, paddingTop: 12 }}>{sec.title}</td>
      </tr>
      {rows.map((r) => (
        <tr key={r.cat} onClick={() => onDrill(r.cat)} style={{ cursor: 'pointer', color: T1 }}>
          <td style={{ ...cell, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: colors[r.cat] || FLUX, flexShrink: 0 }} />
            <span>{CLAUSE_LABELS[r.cat] || r.cat}</span>
            {r.unmapped && (
              <span style={{ fontSize: 9, fontWeight: 700, color: '#E8A13A', border: '1px solid rgba(232,161,58,0.5)', borderRadius: 999, padding: '1px 6px', letterSpacing: 0.4 }}>UNMAPPED</span>
            )}
          </td>
          <td style={num}>{ZAR(r.v / wn)}</td>
          <td style={num}>{ZAR(r.v)}</td>
        </tr>
      ))}
      <tr style={{ ...subRow, color: T1 }}>
        <td style={cell}>{sec.total}</td>
        <td style={num}>{ZAR(total / wn)}</td>
        <td style={num}>{ZAR(total)}</td>
      </tr>
    </>
  );
}

// Full transaction list for one category, newest first.
function CategoryDrill({ cat, onClose }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [accts, setAccts] = useState({});
  const [busy, setBusy] = useState({});     // id -> saving
  const [removed, setRemoved] = useState([]); // ids marked not-mine this session
  const [sort, setSort] = useState('newest'); // newest | oldest | amount
  const [openId, setOpenId] = useState(null); // row with split editor open
  const [draft, setDraft] = useState({ p: '100', k: '0' });
  const [catDraft, setCatDraft] = useState({ p: '100', k: '0' }); // whole-category split
  const [catBusy, setCatBusy] = useState(false);
  const [catSaved, setCatSaved] = useState(false);
  const [catList, setCatList] = useState([]); // all category names for the dropdown
  const [confirm, setConfirm] = useState(null); // {t, action:'notmine'|'undo', cp, count}

  useEffect(() => {
    api.get('/flux/accounts').then(({ data }) => {
      const m = {}; (data.accounts || []).forEach((a) => { m[a.id] = a; }); setAccts(m);
    }).catch(() => {});
    api.get('/flux/categories').then(({ data }) => {
      const cs = (data.categories || []).filter((x) => !x.system);
      setCatList(cs.map((x) => x.name));
      const c = cs.find((x) => x.name === cat.name);
      if (c) setCatDraft({ p: String(c.personal_pct ?? 100), k: String(c.kids_pct ?? 0) });
    }).catch(() => {});
    const q = cat.trip_id ? `trip_id=${cat.trip_id}` : cat.month ? `month=${cat.month}` : `category=${encodeURIComponent(cat.name)}`;
    api.get(`/flux/transactions?${q}&limit=1000&_=${Date.now()}`)
      .then(({ data }) => { setRows(data.transactions || []); setTotal(data.total || 0); })
      .catch(() => setRows([]));
  }, [cat]);

  const applyCategory = async () => {
    const p = Math.max(0, Math.min(100, Number(catDraft.p) || 0));
    const k = Math.max(0, Math.min(100, Number(catDraft.k) || 0));
    setCatBusy(true); setCatSaved(false);
    try {
      await api.patch(`/flux/categories/${encodeURIComponent(cat.name)}/split`, { personal_pct: p, kids_pct: k });
      const { data } = await api.get(`/flux/transactions?category=${encodeURIComponent(cat.name)}&limit=1000&_=${Date.now()}`);
      setRows(data.transactions || []);
      setCatSaved(true);
    } catch { /* */ } finally { setCatBusy(false); }
  };

  const applyNotMine = async (t) => {
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.post(`/flux/transactions/${t.id}/resolve`, { werner_cost: false });
      setRows((rs) => rs.map((x) => (x.id === t.id ? { ...x, werner_cost: false, personal_pct: 0, kids_pct: 0 } : x)));
      setRemoved((r) => [...r, t.id]);
    } catch { /* leave as-is */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
  };
  const applyUndo = async (t) => {
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.post(`/flux/transactions/${t.id}/resolve`, { werner_cost: true });
      setRows((rs) => rs.map((x) => (x.id === t.id ? { ...x, werner_cost: true, personal_pct: 100 } : x)));
      setRemoved((r) => r.filter((id) => id !== t.id));
    } catch { /* */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
  };
  // Before flipping one line, see if the same counterparty appears elsewhere and
  // offer to apply the change to all of them.
  const markNotMine = async (t) => {
    const cp = (t.counterparty || '').trim();
    if (!cp) return applyNotMine(t);
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      const { data } = await api.get(`/flux/transactions/similar-counts?counterparty=${encodeURIComponent(cp)}&_=${Date.now()}`);
      if ((data.mine || 0) > 1) { setConfirm({ t, action: 'notmine', cp, count: data.mine }); return; }
    } catch { /* fall through to single */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
    applyNotMine(t);
  };
  const undo = async (t) => {
    const cp = (t.counterparty || '').trim();
    if (!cp) return applyUndo(t);
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      const { data } = await api.get(`/flux/transactions/similar-counts?counterparty=${encodeURIComponent(cp)}&_=${Date.now()}`);
      if ((data.not_mine || 0) > 1) { setConfirm({ t, action: 'undo', cp, count: data.not_mine }); return; }
    } catch { /* fall through to single */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
    applyUndo(t);
  };
  // "Not travel": home/family spend that fell inside a trip window. Unstamp it back to
  // its own category and make it stick, offering to do the same for lookalikes.
  const applyExclude = async (t) => {
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.post(`/flux/transactions/${t.id}/exclude-trip`);
      setRows((rs) => rs.filter((x) => x.id !== t.id)); // leaves travel
    } catch { /* */ } finally { setBusy((b) => ({ ...b, [t.id]: false })); }
  };
  const excludeTrip = async (t) => {
    const cp = (t.counterparty || '').trim();
    if (!cp) return applyExclude(t);
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      const { data } = await api.get(`/flux/transactions/similar-counts?counterparty=${encodeURIComponent(cp)}&_=${Date.now()}`);
      if ((data.in_trip || 0) > 1) { setConfirm({ t, action: 'nottravel', cp, count: data.in_trip }); return; }
    } catch { /* fall through to single */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
    applyExclude(t);
  };
  const applyJustThis = () => {
    if (!confirm) return;
    const { t, action, category } = confirm; setConfirm(null);
    if (action === 'undo') applyUndo(t);
    else if (action === 'category') applyCategoryChange(t, category);
    else if (action === 'nottravel') applyExclude(t);
    else applyNotMine(t);
  };
  const applyBulk = async () => {
    if (!confirm) return;
    const { cp, action, category } = confirm; setConfirm(null);
    try {
      if (action === 'category') {
        await api.post('/flux/transactions/bulk-categorize', { counterparty: cp, category });
      } else if (action === 'nottravel') {
        await api.post('/flux/transactions/bulk-exclude-trip', { counterparty: cp });
      } else {
        await api.post('/flux/transactions/bulk-resolve', { counterparty: cp, werner_cost: action === 'undo' });
      }
      const q = cat.trip_id ? `trip_id=${cat.trip_id}` : cat.month ? `month=${cat.month}` : `category=${encodeURIComponent(cat.name)}`;
      const { data } = await api.get(`/flux/transactions?${q}&limit=1000&_=${Date.now()}`);
      const next = data.transactions || [];
      setRows(next);
      setRemoved(next.filter((x) => (x.personal_pct ?? 100) === 0).map((x) => x.id));
    } catch { /* */ }
  };

  const openSplit = (t) => { setOpenId(t.id); setDraft({ p: String(t.personal_pct ?? 100), k: String(t.kids_pct ?? 0) }); };
  const saveSplit = async (t) => {
    const p = Math.max(0, Math.min(100, Number(draft.p) || 0));
    const k = Math.max(0, Math.min(100, Number(draft.k) || 0));
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.patch(`/flux/transactions/${t.id}/split`, { personal_pct: p, kids_pct: k });
      setRows((rs) => rs.map((x) => (x.id === t.id ? { ...x, personal_pct: p, kids_pct: k, split_source: 'manual' } : x)));
      setOpenId(null);
    } catch { /* */ } finally { setBusy((b) => ({ ...b, [t.id]: false })); }
  };

  const applyCategoryChange = async (t, newCat) => {
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      await api.post(`/flux/transactions/${t.id}/resolve`, { category: newCat, werner_cost: t.werner_cost });
      setRows((rs) => rs.filter((x) => x.id !== t.id)); // moved to another category
      setOpenId(null);
    } catch { /* */ } finally { setBusy((b) => ({ ...b, [t.id]: false })); }
  };
  const changeCategory = async (t, newCat) => {
    if (!newCat || newCat === t.category) return;
    const cp = (t.counterparty || '').trim();
    if (!cp) return applyCategoryChange(t, newCat);
    setBusy((b) => ({ ...b, [t.id]: true }));
    try {
      const { data } = await api.get(`/flux/transactions/similar-counts?counterparty=${encodeURIComponent(cp)}&category=${encodeURIComponent(newCat)}&_=${Date.now()}`);
      if ((data.to_move || 0) > 1) { setConfirm({ t, action: 'category', cp, count: data.to_move, category: newCat }); setOpenId(null); return; }
    } catch { /* fall through to single */ } finally {
      setBusy((b) => ({ ...b, [t.id]: false }));
    }
    applyCategoryChange(t, newCat);
  };

  const yourSpend = (rows || []).reduce(
    (a, t) => a + (t.amount < 0 ? -t.amount * (t.personal_pct || 0) / 100 : 0), 0);

  const sorted = [...(rows || [])].sort((a, b) => {
    if (sort === 'amount') return Math.abs(b.amount) - Math.abs(a.amount);
    if (sort === 'oldest') return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
    return a.date > b.date ? -1 : a.date < b.date ? 1 : 0; // newest
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,15,0.975)', zIndex: 50 }}>
      <div style={{ maxWidth: 640, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', padding: '16px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: cat.color, flexShrink: 0 }} />
          <div style={{ flex: 1, color: T1, fontSize: 18, fontWeight: 700 }}>{cat.name || cat.label}</div>
          <button onClick={onClose} style={navBtn}><X size={18} /></button>
        </div>
        <div style={{ color: T3, fontSize: 12, marginBottom: 10 }}>
          {total} transactions  .  {ZAR(yourSpend)} your spend {rows && rows.length < total ? `(showing latest ${rows.length})` : ''}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[['newest', 'Newest'], ['oldest', 'Oldest'], ['amount', 'Amount']].map(([v, l]) => (
            <button key={v} onClick={() => setSort(v)} style={{
              ...pill, padding: '4px 11px', fontSize: 12,
              ...(sort === v ? { background: `${FLUX}22`, color: FLUX, borderColor: `${FLUX}55` } : {}),
            }}>{l}</button>
          ))}
        </div>
        {cat.name && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '9px 11px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
          <span style={{ color: T2, fontSize: 12, fontWeight: 600 }}>Whole category</span>
          <label style={{ color: T3, fontSize: 11 }}>you%</label>
          <input type="number" min="0" max="100" value={catDraft.p}
            onChange={(e) => { setCatSaved(false); setCatDraft((d) => ({ ...d, p: e.target.value })); }}
            style={{ ...inputStyle, width: 52, padding: '5px 6px' }} />
          <label style={{ color: T3, fontSize: 11 }}>kids%</label>
          <input type="number" min="0" max="100" value={catDraft.k}
            onChange={(e) => { setCatSaved(false); setCatDraft((d) => ({ ...d, k: e.target.value })); }}
            style={{ ...inputStyle, width: 52, padding: '5px 6px' }} />
          <button onClick={applyCategory} disabled={catBusy}
            style={{ ...pill, padding: '5px 12px', fontSize: 12, background: FLUX, color: '#fff', borderColor: FLUX }}>
            {catBusy ? '…' : catSaved ? '✓ Applied' : 'Apply to all'}
          </button>
          <span style={{ color: T3, fontSize: 10, width: '100%' }}>Applies to every line except ones you set individually.</span>
        </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 24 }}>
          {rows === null ? <ZLoadingBar /> : rows.length === 0 ? (
            <div style={{ color: T3, fontSize: 13 }}>No transactions.</div>
          ) : sorted.map((t) => {
            const p = t.personal_pct ?? 100, k = t.kids_pct ?? 0;
            const gone = removed.includes(t.id) || p === 0;
            const open = openId === t.id;
            const splitLabel = p === 0 ? 'excluded' : (p === 100 && k === 0) ? '' : `${p}% you${k ? `, ${k}% kids` : ''}`;
            return (
              <div key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', opacity: gone ? 0.5 : 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 0' }}>
                  <div style={{ color: T3, fontSize: 11, width: 58, flexShrink: 0 }}>{t.date}</div>
                  <div onClick={() => (open ? setOpenId(null) : openSplit(t))} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <div style={{ color: T1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: gone ? 'line-through' : 'none' }}>
                      {t.counterparty || t.description}
                    </div>
                    <div style={{ color: T3, fontSize: 10, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {t.category && t.category !== cat.name && (
                        <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.07)', color: T2, fontSize: 10 }}>{t.category}</span>
                      )}
                      <span>
                        {accts[t.account_id]?.label || accts[t.account_id]?.entity || ''}
                        {splitLabel ? `  ·  ${splitLabel}` : ''}
                      </span>
                    </div>
                  </div>
                  <div style={{ color: T1, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{ZAR(Math.abs(t.amount))}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                    {t.trip_id && (
                      <button onClick={() => excludeTrip(t)} disabled={busy[t.id]}
                        style={{ ...pill, padding: '3px 9px', fontSize: 11, color: '#E8A13A', borderColor: 'rgba(232,161,58,0.4)' }}>
                        not travel
                      </button>
                    )}
                    <button onClick={() => (gone ? undo(t) : markNotMine(t))} disabled={busy[t.id]}
                      style={{ ...pill, padding: '3px 9px', fontSize: 11,
                        color: gone ? '#5AA9A3' : T3, borderColor: gone ? 'rgba(90,169,163,0.4)' : 'rgba(255,255,255,0.14)' }}>
                      {busy[t.id] ? '…' : gone ? 'undo' : 'not mine'}
                    </button>
                  </div>
                </div>
                {open && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 0 10px 68px', flexWrap: 'wrap' }}>
                    <select value={t.category || ''} onChange={(e) => changeCategory(t, e.target.value)}
                      style={{ ...selectStyle, padding: '5px 6px', maxWidth: 160 }}>
                      {catList.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <label style={{ color: T3, fontSize: 11 }}>you%</label>
                    <input type="number" min="0" max="100" value={draft.p} onChange={(e) => setDraft((d) => ({ ...d, p: e.target.value }))}
                      style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
                    <label style={{ color: T3, fontSize: 11 }}>kids%</label>
                    <input type="number" min="0" max="100" value={draft.k} onChange={(e) => setDraft((d) => ({ ...d, k: e.target.value }))}
                      style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
                    <button onClick={() => saveSplit(t)} disabled={busy[t.id]}
                      style={{ ...pill, padding: '5px 12px', fontSize: 12, background: FLUX, color: '#fff', borderColor: FLUX }}>
                      {busy[t.id] ? '…' : 'Save'}
                    </button>
                    <button onClick={() => setOpenId(null)} style={{ ...pill, padding: '5px 10px', fontSize: 12 }}>Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#16161c', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 20, maxWidth: 380, width: '100%' }}>
            <div style={{ color: T1, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              {confirm.action === 'undo' ? 'Restore similar entries?'
                : confirm.action === 'category' ? 'Recategorize similar entries too?'
                : confirm.action === 'nottravel' ? 'Move similar entries out of travel?'
                : 'Mark similar entries too?'}
            </div>
            <div style={{ color: T2, fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              {confirm.count} {confirm.action === 'nottravel' ? 'in-trip ' : ''}entries match <b style={{ color: T1 }}>{confirm.cp}</b>
              {confirm.action === 'undo'
                ? ' and are currently excluded. Bring them all back in?'
                : confirm.action === 'category'
                ? <> . Move them all to <b style={{ color: T1 }}>{confirm.category}</b>?</>
                : confirm.action === 'nottravel'
                ? ' . Move them all back to their own category and keep them out of trips?'
                : ' and are counted as yours. Mark them all not mine?'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={applyBulk} style={{ ...pill, flex: 1, padding: '9px', fontSize: 13, background: FLUX, color: '#fff', borderColor: FLUX, justifyContent: 'center' }}>
                {confirm.action === 'undo' ? `Restore all ${confirm.count}`
                  : confirm.action === 'category' ? `Move all ${confirm.count}`
                  : confirm.action === 'nottravel' ? `Move all ${confirm.count}`
                  : `All ${confirm.count}`}
              </button>
              <button onClick={applyJustThis} style={{ ...pill, flex: 1, padding: '9px', fontSize: 13, justifyContent: 'center' }}>Just this one</button>
            </div>
            <button onClick={() => setConfirm(null)} style={{ ...pill, width: '100%', marginTop: 8, padding: '8px', fontSize: 12, color: T3, justifyContent: 'center' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Stacked monthly bars (kids + you), as inline SVG. Tap a bar to drill into that month.
function TrendChart({ series, onPick }) {
  const W = 700, H = 170, padB = 18, padT = 8;
  const max = Math.max(...series.map((s) => s.total || 0), 1);
  const bw = W / series.length;
  const y = (v) => (H - padB) - (v / max) * (H - padB - padT);
  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ display: 'block', height: 170 }}>
        {series.map((s, i) => {
          const x = i * bw + 1.5, w = Math.max(1, bw - 3);
          // Stack bottom->top: kids everyday (gold), you everyday (red), travel (blue).
          const total = s.total || 0;
          const travel = (s.categories && s.categories.travel) || 0;
          const travelKids = (s.categories_kids && s.categories_kids.travel) || 0;
          const everyday = total - travel;
          const kidsEveryday = Math.max(0, (s.kids || 0) - travelKids);
          const seg = (lo, hi, fill, op) => (
            <rect x={x} y={y(hi)} width={w} height={Math.max(0, y(lo) - y(hi))} fill={fill} opacity={op} />
          );
          return (
            <g key={i} onClick={() => onPick && onPick(s.month)} style={{ cursor: 'pointer' }}>
              <rect x={i * bw} y="0" width={bw} height={H} fill="transparent" />
              {seg(0, kidsEveryday, '#E8A13A', 0.6)}
              {seg(kidsEveryday, everyday, FLUX, 0.38)}
              {seg(everyday, total, TRAVEL_BLUE, 0.8)}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', marginTop: 2 }}>
        {series.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: T3, overflow: 'hidden' }}>
            {i % 3 === 0 ? s.month.slice(2) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== REVIEW ====================
const REVIEW_BLOCK = 10;

function groupExceptions(rows) {
  // One card per counterparty; lines with no counterparty stand alone.
  const map = new Map();
  rows.forEach((r) => {
    const key = (r.counterparty || '').trim() ? `cp:${r.counterparty.trim()}` : `id:${r.id}`;
    if (!map.has(key)) map.set(key, { key, lead: r, lines: [] });
    map.get(key).lines.push(r);
  });
  return Array.from(map.values());
}

function ReviewTab({ onCount }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState({}); // group key -> { category, werner, skip }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [lastMsg, setLastMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/flux/exceptions'), api.get('/flux/categories')])
      .then(([ex, ct]) => {
        setRows(ex.data.exceptions || []);
        setCats((ct.data.categories || []).filter((c) => !c.system));
        onCount?.(ex.data.count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupExceptions(rows), [rows]);
  const block = groups.filter((g) => !decisions[g.key]?.skip).slice(0, REVIEW_BLOCK);

  const decisionFor = (g) => decisions[g.key] || {
    category: g.lead.category || (cats[0] && cats[0].name) || '',
    werner: g.lead.werner_cost !== false,
  };
  const setDecision = (g, patch) => setDecisions((d) => ({ ...d, [g.key]: { ...decisionFor(g), ...patch } }));

  const confirmBlock = async () => {
    if (!block.length) return;
    setBusy(true); setErr(''); setLastMsg('');
    const items = block.map((g) => {
      const d = decisionFor(g);
      return { id: g.lead.id, category: d.category, werner_cost: d.werner, apply_to_matching: true };
    });
    try {
      const { data } = await api.post('/flux/transactions/resolve-batch', { items });
      const doneKeys = new Set(block.map((g) => g.key));
      const doneIds = new Set(block.flatMap((g) => g.lines.map((l) => l.id)));
      setRows((r) => {
        const next = r.filter((x) => !doneIds.has(x.id));
        onCount?.(data.remaining_exceptions ?? next.length);
        return next;
      });
      setDecisions((d) => Object.fromEntries(Object.entries(d).filter(([k]) => !doneKeys.has(k))));
      setLastMsg(`${data.confirmed} line${data.confirmed === 1 ? '' : 's'} confirmed${data.rules_created ? `, ${data.rules_created} rule${data.rules_created === 1 ? '' : 's'} remembered` : ''}.`);
      if ((data.remaining_exceptions ?? 0) !== rows.length - doneIds.size) load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save. Check your connection and retry.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <ZLoadingBar />;
  if (rows.length === 0) {
    return <ZEmptyState mark="F" accent={FLUX} message="Nothing to review" hint="Claude routed no exceptions. You are all caught up." />;
  }

  const lineTotal = block.reduce((n, g) => n + g.lines.length, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 140 }}>
      <div style={{ color: T3, fontSize: 12, marginBottom: 4 }}>
        {groups.length} counterpart{groups.length === 1 ? 'y' : 'ies'} ({rows.length} lines) to review, {block.length} at a time.
        Set category and my-cost where the guess is wrong, then confirm the block. Each decision applies to every line of that counterparty and is remembered.
      </div>
      {err && (
        <div style={{ color: '#fff', background: '#7a2b2b', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} /> {err}
        </div>
      )}
      {lastMsg && <div style={{ color: T2, fontSize: 12 }}>{lastMsg}</div>}
      {block.map((g) => (
        <ExceptionCard key={g.key} group={g} cats={cats} decision={decisionFor(g)}
          onChange={(patch) => setDecision(g, patch)} onSkip={() => setDecision(g, { skip: true })} />
      ))}
      <div style={{ position: 'sticky', bottom: 88, display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
        <button onClick={confirmBlock} disabled={busy || !block.length} style={{
          ...pill, background: FLUX, color: '#fff', borderColor: FLUX, padding: '10px 18px',
          display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
        }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Confirm {block.length} ({lineTotal} line{lineTotal === 1 ? '' : 's'})
        </button>
      </div>
    </div>
  );
}

function ExceptionCard({ group, cats, decision, onChange, onSkip }) {
  const { lead, lines } = group;
  const total = lines.reduce((n, l) => n + Math.abs(l.amount), 0);
  const [open, setOpen] = useState(false);
  return (
    <ZCard accent={FLUX}>
      <div style={{ padding: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <div style={{ color: T1, fontSize: 14, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lead.counterparty || lead.description || '(no description)'}
          </div>
          <div style={{ color: lead.amount < 0 ? T1 : '#5AA9A3', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
            {ZAR(total)}
          </div>
        </div>
        <div style={{ color: T3, fontSize: 11, marginTop: 2 }}>
          {lines.length === 1
            ? <>{lead.date}  .  {lead.description}</>
            : <span onClick={() => setOpen((o) => !o)} style={{ cursor: 'pointer' }}>
                {lines.length} lines, {lines[lines.length - 1].date} to {lines[0].date} {open ? '▾' : '▸'}
              </span>}
        </div>
        {open && lines.length > 1 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {lines.map((l) => (
              <div key={l.id} style={{ color: T3, fontSize: 11, display: 'flex', gap: 8 }}>
                <span style={{ flexShrink: 0 }}>{l.date}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</span>
                <span style={{ flexShrink: 0 }}>{ZAR(Math.abs(l.amount))}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={decision.category} onChange={(e) => onChange({ category: e.target.value })} style={selectStyle}>
            {cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <button onClick={() => onChange({ werner: !decision.werner })} style={{
            ...pill, borderColor: decision.werner ? `${FLUX}66` : 'rgba(255,255,255,0.1)',
            color: decision.werner ? FLUX : T3, background: decision.werner ? `${FLUX}18` : 'transparent',
          }}>
            {decision.werner ? 'my cost' : 'not mine'}
          </button>
          <button onClick={onSkip} title="Leave for later" style={{ ...pill, marginLeft: 'auto', color: T3 }}>
            skip
          </button>
        </div>
      </div>
    </ZCard>
  );
}

// ==================== INGEST ====================
function IngestTab() {
  const [accounts, setAccounts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get('/flux/accounts'), api.get('/flux/categories')])
      .then(([a, c]) => { setAccounts(a.data.accounts || []); setCats(c.data.categories || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const locked = cats.some((c) => c.locked && !c.system);

  if (loading) return <ZLoadingBar />;
  return (
    <div style={{ paddingBottom: 120, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <AccountRegistry accounts={accounts} onChange={load} />
      {accounts.length > 0 && <FileDrop accounts={accounts} onIngested={load} />}
      {accounts.length > 0 && !locked && <TaxonomyFlow onLocked={load} />}
      {accounts.length > 0 && <DataTools />}
    </div>
  );
}

function DataTools() {
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const inputRef = useRef(null);

  const doExport = async () => {
    setBusy('export'); setMsg('');
    try {
      const res = await api.get('/flux/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'flux_export.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Export failed.'); } finally { setBusy(''); }
  };
  const doImport = async (file) => {
    if (!file) return;
    setBusy('import'); setMsg('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/flux/import', fd);
      setMsg(`Updated ${data.updated}, unchanged ${data.unchanged}${data.not_found ? `, ${data.not_found} not found` : ''}.`);
    } catch (e) { setMsg(e.response?.data?.detail || 'Import failed.'); } finally { setBusy(''); if (inputRef.current) inputRef.current.value = ''; }
  };

  return (
    <div>
      <SectionHeader title="Spreadsheet" />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={doExport} disabled={!!busy} style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {busy === 'export' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Export CSV
        </button>
        <button onClick={() => inputRef.current?.click()} disabled={!!busy} style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {busy === 'import' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import CSV
        </button>
        <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => doImport(e.target.files[0])} />
      </div>
      <div style={{ color: T3, fontSize: 11, marginTop: 8 }}>
        Export every transaction, edit the category / werner_cost / personal_pct / kids_pct columns (in Claude or a spreadsheet), then re-import. Rows match by id; other columns are ignored.
      </div>
      {msg && <div style={{ color: T2, fontSize: 12, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

function AccountRegistry({ accounts, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: '', institution: 'fnb', account_type: 'current', entity: 'personal' });
  const feedback = useConfirmation();

  const setNumber = async (a) => {
    const current = a.account_number || '';
    const v = window.prompt(`Account number for ${a.label} (as printed on the statement)`, current);
    if (v === null || v === current) return;
    try {
      await api.patch(`/flux/accounts/${a.id}`, { account_number: v });
      onChange();
    } catch { feedback.fail(); }
  };

  const create = async () => {
    if (!form.label.trim()) return;
    try {
      await api.post('/flux/accounts', form);
      feedback.confirm();
      setForm({ label: '', institution: 'fnb', account_type: 'current', entity: 'personal' });
      setAdding(false);
      onChange();
    } catch { feedback.fail(); }
  };

  return (
    <div>
      <SectionHeader title="Accounts" action={
        <button onClick={() => setAdding((a) => !a)} style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Plus size={14} /> Add
        </button>
      } />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {accounts.map((a) => (
          <ZCard key={a.id} accent={FLUX}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
              <div>
                <div style={{ color: T1, fontSize: 14, fontWeight: 600 }}>{a.label}</div>
                <div style={{ color: T3, fontSize: 11 }}>
                  {a.institution} . {a.account_type} . {a.entity}
                  {a.account_number ? ` . ...${a.account_number.slice(-4)}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ color: T3, fontSize: 11 }}>{a.last_ingested_period || 'no data'}</div>
                <button onClick={() => setNumber(a)} style={{ ...pill, fontSize: 11 }} title="Set account number for auto-detect">#</button>
              </div>
            </div>
          </ZCard>
        ))}
        {accounts.length === 0 && !adding && (
          <div style={{ color: T3, fontSize: 13 }}>No accounts yet. Add one to start ingesting.</div>
        )}
      </div>

      {adding && (
        <ZCard accent={FLUX} confirmationState={feedback.state}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 2 }}>
            <input placeholder="Label (e.g. Fenix FNB)" value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} style={selectStyle}>
                <option value="fnb">fnb</option><option value="discovery">discovery</option>
              </select>
              <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} style={selectStyle}>
                <option value="current">current</option><option value="credit_card">credit_card</option>
              </select>
              <select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={selectStyle}>
                {['personal', 'fenix', 'filius', 'makulu', 'astrax', 'kayax'].map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <button onClick={create} style={{ ...pill, background: FLUX, color: '#fff', borderColor: FLUX }}>Create account</button>
          </div>
        </ZCard>
      )}
    </div>
  );
}

function IngestResultRows({ files }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {(files || []).map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: f.error ? FLUX : T2 }}>
          <FileText size={13} />
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.file}{f.account ? <span style={{ color: T3 }}> → {f.account}{f.format ? ` · ${f.format}` : ''}{f.account_created ? ' (account created)' : f.account_learned ? ' (number learned)' : ''}</span> : null}
          </span>
          {f.error ? <span>{f.error}</span>
            : <span style={{ color: T3, flexShrink: 0 }}>
                {f.rows_stored} stored . {f.rows_deduped} dup . {f.rows_queued} queued
                {f.stored_from ? ` . ${f.stored_from} → ${f.stored_to}` : f.rows_parsed && !f.rows_stored ? ' . nothing new' : ''}
              </span>}
        </div>
      ))}
    </div>
  );
}

function IngestHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get('/flux/ingests?limit=10').then(({ data }) => setRows(data.ingests || [])).catch(() => {});
  }, [refreshKey]);
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div onClick={() => setOpen((o) => !o)} style={{ color: T3, fontSize: 12, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Recent ingests ({rows.length}) {open ? '▾' : '▸'}
      </div>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {rows.map((r) => (
            <div key={r.id}>
              <div style={{ color: T3, fontSize: 11, marginBottom: 4 }}>
                {new Date(r.at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })} . {r.total_stored} stored
              </div>
              <IngestResultRows files={r.files} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FileDrop({ accounts, onIngested }) {
  const [accountId, setAccountId] = useState('');  // '' = auto-detect from the statement
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true); setResults(null);
    const fd = new FormData();
    if (accountId) fd.append('account_id', accountId);
    Array.from(files).forEach((f) => fd.append('files', f));
    try {
      const { data } = await api.post('/flux/ingest', fd);
      if (!data || !Array.isArray(data.files)) {
        setResults({ error: 'server returned no ingest result; nothing was stored' });
      } else {
        setResults(data);
      }
      setHistoryKey((k) => k + 1);
      onIngested();
    } catch (e) {
      setResults({ error: e.response?.data?.detail || 'ingest failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SectionHeader title="Ingest statements" />
      <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={{ ...selectStyle, marginBottom: 10, width: '100%' }}>
        <option value="">Auto-detect account from statement</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>{a.label}{a.account_number ? ` (...${a.account_number.slice(-4)})` : ''}</option>
        ))}
      </select>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        style={{
          border: `1px dashed ${FLUX}66`, borderRadius: 12, padding: '28px 16px', textAlign: 'center',
          cursor: 'pointer', background: `${FLUX}08`, color: T2,
        }}>
        {busy ? <Loader2 size={20} className="animate-spin" style={{ color: FLUX }} />
          : <Upload size={20} style={{ color: FLUX }} />}
        <div style={{ fontSize: 13, marginTop: 8 }}>Drop PDF, XLSX or CSV statements, or tap to choose</div>
        <input ref={inputRef} type="file" multiple accept=".pdf,.xlsx,.csv" style={{ display: 'none' }}
          onChange={(e) => upload(e.target.files)} />
      </div>

      {results && (
        <div style={{ marginTop: 12 }}>
          {results.error ? (
            <div style={{ color: FLUX, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={14} /> {results.error}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <IngestResultRows files={results.files} />
              <div style={{ color: T3, fontSize: 11, marginTop: 4 }}>
                {results.total_stored} stored total{results.claude?.status === 'queued' ? ` . ${results.claude.pending} queued, Claude classifying in the background (check Review in a few minutes)` : results.claude ? ` . ${results.claude.classified} classified, ${results.claude.exceptions} exceptions` : ''}
              </div>
            </div>
          )}
        </div>
      )}
      <IngestHistory refreshKey={historyKey} />
    </div>
  );
}

function TaxonomyFlow({ onLocked }) {
  const [proposal, setProposal] = useState(null);
  const [names, setNames] = useState([]);
  const [busy, setBusy] = useState(false);

  const propose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/flux/propose-taxonomy');
      setProposal(data);
      setNames((data.proposal || []).map((p) => p.name));
    } catch { /* silent */ } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post('/flux/categories/confirm', { names: names.filter((n) => n.trim()) });
      onLocked();
    } catch { /* silent */ } finally { setBusy(false); }
  };

  return (
    <div>
      <SectionHeader title="Taxonomy" />
      {!proposal ? (
        <button onClick={propose} disabled={busy} style={{ ...pill, background: FLUX, color: '#fff', borderColor: FLUX, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Propose categories
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ color: T3, fontSize: 12 }}>Edit, merge, or remove before locking. This is one time.</div>
          {names.map((n, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={n} onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => setNames(names.filter((_, j) => j !== i))} style={navBtn}><Trash2 size={14} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setNames([...names, ''])} style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Plus size={14} /> Add</button>
            <button onClick={confirm} disabled={busy} style={{ ...pill, marginLeft: 'auto', background: FLUX, color: '#fff', borderColor: FLUX }}>
              {busy ? 'Locking...' : 'Lock taxonomy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== SPLITS ====================
function SplitsTab() {
  const [cats, setCats] = useState([]);
  const [vals, setVals] = useState({});   // name -> { p, k } as strings
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const [renaming, setRenaming] = useState(null); // category id being renamed
  const [rdraft, setRdraft] = useState('');
  const [rbusy, setRbusy] = useState(false);
  const [rerr, setRerr] = useState('');

  const doRename = async (c) => {
    const nn = rdraft.trim();
    if (!nn || nn.toLowerCase() === c.name.toLowerCase()) { setRenaming(null); setRerr(''); return; }
    setRbusy(true); setRerr('');
    try {
      await api.patch(`/flux/categories/${encodeURIComponent(c.name)}/rename`, { new_name: nn });
      setRenaming(null); setRerr('');
      load();
    } catch (e) {
      setRerr(e?.response?.data?.detail || 'rename failed');
    } finally { setRbusy(false); }
  };

  const load = useCallback(() => {
    api.get('/flux/categories')
      .then(({ data }) => {
        const cs = (data.categories || []).filter((c) => !c.system);
        setCats(cs);
        const v = {};
        cs.forEach((c) => { v[c.name] = { p: String(c.personal_pct ?? 100), k: String(c.kids_pct ?? 0), clause: c.clause || '' }; });
        setVals(v);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setVal = (name, f, value) => {
    setSaved(false); setError(false);
    setVals((s) => ({ ...s, [name]: { ...s[name], [f]: value } }));
  };

  const orig = (c) => ({ p: c.personal_pct ?? 100, k: c.kids_pct ?? 0, clause: c.clause || '' });
  const splitChanged = (c) => { const v = vals[c.name] || {}; return Number(v.p) !== orig(c).p || Number(v.k) !== orig(c).k; };
  const clauseChanged = (c) => { const v = vals[c.name] || {}; return (v.clause || '') !== orig(c).clause; };
  const changed = cats.filter((c) => splitChanged(c) || clauseChanged(c));

  const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
  const [nc, setNc] = useState({ name: '', p: '100', k: '0', clause: '' });
  const [ncBusy, setNcBusy] = useState(false);
  const [ncErr, setNcErr] = useState('');
  const addCategory = async () => {
    const name = nc.name.trim();
    if (!name || ncBusy) return;
    setNcBusy(true); setNcErr('');
    try {
      await api.post('/flux/categories', {
        name, personal_pct: clamp(nc.p), kids_pct: clamp(nc.k), clause: nc.clause || null,
      });
      setNc({ name: '', p: '100', k: '0', clause: '' });
      load();
    } catch (e) {
      setNcErr(e?.response?.data?.detail || 'could not add category');
    } finally { setNcBusy(false); }
  };
  const saveAll = async () => {
    if (!changed.length || saving) return;
    setSaving(true); setError(false);
    try {
      await Promise.all(changed.flatMap((c) => {
        const calls = [];
        if (splitChanged(c)) calls.push(api.patch(
          `/flux/categories/${encodeURIComponent(c.name)}/split`,
          { personal_pct: clamp(vals[c.name].p), kids_pct: clamp(vals[c.name].k) },
        ));
        if (clauseChanged(c)) calls.push(api.patch(
          `/flux/categories/${encodeURIComponent(c.name)}/clause`,
          { clause: vals[c.name].clause || null },
        ));
        return calls;
      }));
      setSaved(true);
      load(); // reflect the persisted values so the dashboard and rows stay in sync
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ZLoadingBar />;
  return (
    <div style={{ paddingBottom: 140 }}>
      <div style={{ color: T3, fontSize: 12, marginBottom: 14 }}>
        You% is your share of each category. Kids% is the share of that which is the kids'. The rest is company. The last box is the Consent Paper clause the category reports under. Adjust any values, then Save all. Tap the pencil to rename a category.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cats.map((c) => {
          const v = vals[c.name] || { p: '100', k: '0', clause: '' };
          const rowChanged = splitChanged(c) || clauseChanged(c);
          return (
            <ZCard key={c.id} accent={c.color || FLUX} tinted={rowChanged}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 2 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: c.color || FLUX, flexShrink: 0 }} />
                {!c.clause && <span title="No clause mapped" style={{ width: 6, height: 6, borderRadius: 3, background: '#E8A13A', flexShrink: 0, marginLeft: -6 }} />}
                {renaming === c.id ? (
                  <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
                    <input autoFocus value={rdraft} onChange={(e) => setRdraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') doRename(c); if (e.key === 'Escape') { setRenaming(null); setRerr(''); } }}
                      style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '5px 8px' }} />
                    <button onClick={() => doRename(c)} disabled={rbusy}
                      style={{ ...pill, padding: '4px 10px', fontSize: 11, background: FLUX, color: '#fff', borderColor: FLUX }}>{rbusy ? '…' : 'Save'}</button>
                    <button onClick={() => { setRenaming(null); setRerr(''); }}
                      style={{ ...pill, padding: '4px 8px', fontSize: 11 }}>×</button>
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ color: T1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    <button onClick={() => { setRenaming(c.id); setRdraft(c.name); setRerr(''); }} title="Rename"
                      style={{ background: 'none', border: 'none', color: T3, cursor: 'pointer', display: 'inline-flex', padding: 2, flexShrink: 0 }}><Pencil size={12} /></button>
                  </div>
                )}
                <label style={{ color: T3, fontSize: 11 }}>you%</label>
                <input type="number" min="0" max="100" value={v.p} onChange={(e) => setVal(c.name, 'p', e.target.value)}
                  style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
                <label style={{ color: T3, fontSize: 11 }}>kids%</label>
                <input type="number" min="0" max="100" value={v.k} onChange={(e) => setVal(c.name, 'k', e.target.value)}
                  style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
                <select value={v.clause || ''} onChange={(e) => setVal(c.name, 'clause', e.target.value)} title="Consent Paper clause"
                  style={{ ...selectStyle, padding: '5px 4px', fontSize: 11, width: 76 }}>
                  {CLAUSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  <option value="">unmapped</option>
                </select>
              </div>
              {renaming === c.id && rerr && (
                <div style={{ color: '#E27B7B', fontSize: 11, padding: '4px 2px 0 22px' }}>{rerr}</div>
              )}
            </ZCard>
          );
        })}
      </div>
      {/* add a category after taxonomy lock */}
      <div style={{ marginTop: 14 }}>
        <div style={{ color: T3, fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Add category</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <input placeholder="name" value={nc.name} onChange={(e) => { setNc((s) => ({ ...s, name: e.target.value })); setNcErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
            style={{ ...inputStyle, flex: 1, minWidth: 120, padding: '5px 8px' }} />
          <label style={{ color: T3, fontSize: 11 }}>you%</label>
          <input type="number" min="0" max="100" value={nc.p} onChange={(e) => setNc((s) => ({ ...s, p: e.target.value }))}
            style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
          <label style={{ color: T3, fontSize: 11 }}>kids%</label>
          <input type="number" min="0" max="100" value={nc.k} onChange={(e) => setNc((s) => ({ ...s, k: e.target.value }))}
            style={{ ...inputStyle, width: 54, padding: '5px 6px' }} />
          <select value={nc.clause} onChange={(e) => setNc((s) => ({ ...s, clause: e.target.value }))}
            style={{ ...selectStyle, padding: '5px 4px', fontSize: 11, width: 76 }}>
            {CLAUSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            <option value="">unmapped</option>
          </select>
          <button onClick={addCategory} disabled={!nc.name.trim() || ncBusy}
            style={{ ...pill, padding: '5px 12px', fontSize: 12, background: nc.name.trim() ? FLUX : 'transparent', color: nc.name.trim() ? '#fff' : T3, borderColor: nc.name.trim() ? FLUX : 'rgba(255,255,255,0.12)' }}>
            {ncBusy ? '…' : 'Add'}
          </button>
        </div>
        {ncErr && <div style={{ color: '#E27B7B', fontSize: 11, marginTop: 4 }}>{ncErr}</div>}
      </div>
      <div style={{ position: 'sticky', bottom: 16, marginTop: 18, display: 'flex', justifyContent: 'center' }}>
        <button onClick={saveAll} disabled={!changed.length || saving} style={{
          ...pill, padding: '11px 22px', fontSize: 14, fontWeight: 600,
          background: error ? '#7a2b2b' : changed.length ? FLUX : 'rgba(255,255,255,0.06)',
          color: changed.length || error ? '#fff' : T3,
          borderColor: changed.length ? FLUX : 'rgba(255,255,255,0.12)',
          boxShadow: changed.length ? '0 4px 16px rgba(226,75,74,0.35)' : 'none',
        }}>
          {saving ? 'Saving...'
            : error ? 'Save failed, retry'
            : saved && !changed.length ? '✓ Saved'
            : changed.length ? `Save ${changed.length} change${changed.length > 1 ? 's' : ''}`
            : 'No changes'}
        </button>
      </div>
    </div>
  );
}

// ==================== SHARED BITS ====================
function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <div style={{ color: T2, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {action}
    </div>
  );
}

const ENTITY_COLORS = ['#E24B4A', '#E8A13A', '#4C9F70', '#3D7EA6', '#8E6BB5', '#5AA9A3'];
const navBtn = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: T2, width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const pill = { background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999, color: T2, fontSize: 12, padding: '6px 12px', cursor: 'pointer' };
const inputStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: T1, fontSize: 13, padding: '8px 10px', outline: 'none' };
const selectStyle = { ...inputStyle, padding: '7px 8px' };

// ==================== ROOT ====================
export default function Flux() {
  const [tab, setTab] = useState('dashboard');
  const [exCount, setExCount] = useState(0);

  useEffect(() => {
    api.get('/flux/exceptions').then(({ data }) => setExCount(data.count || 0)).catch(() => {});
  }, []);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'review', label: exCount > 0 ? `Review (${exCount})` : 'Review' },
    { id: 'splits', label: 'Splits' },
    { id: 'ingest', label: 'Ingest' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: SYSTEM_COLORS.background }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <img src="/logo-flux.png" alt="Flux" style={{ width: 26, height: 26, borderRadius: 7 }} />
          <span style={{ color: T1, fontSize: 18, fontWeight: 700, letterSpacing: 0.3 }}>Flux</span>
        </div>
        <ZTabs tabs={tabs} activeTab={tab} onChange={setTab} accent={FLUX} />
        <div style={{ marginTop: 20 }}>
          {tab === 'dashboard' && <DashboardTab />}
          {tab === 'review' && <ReviewTab onCount={setExCount} />}
          {tab === 'splits' && <SplitsTab />}
          {tab === 'ingest' && <IngestTab />}
        </div>
      </div>
    </div>
  );
}
